import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'ramda'
import { Menu, Dropdown } from 'antd'
import Button from '../Button'
import Icon from '../Icon'
import Divider from '../Divider'
import withStyle, { MenuItem, Message } from './DropdownMenu.style.js'

const DropdownMenu = ({
  clickHandler,
  options,
  children,
  className,
  text,
  size,
  type,
  link,
  trigger,
  message,
  disabled,
  placement,
  icon,
  iconFill,
  overlay,
  menuClassName,
}) => {
  const renderMessage = useMemo(() => {
    if (!message) return null

    // antd is throwing an error if onClick and onItemHover are not defined
    // might have something to do with everything being rendered via the overlay
    return (
      <>
        <Message onClick={() => {}} onItemHover={() => {}} key={message}>
          {message}
        </Message>
        <Divider collapseMargins={true} />
      </>
    )
  }, [message])

  return (
    <Dropdown
      className={className}
      text={text}
      size={size}
      trigger={[trigger]}
      disabled={disabled}
      placement={placement}
      overlay={
        overlay || (
          <Menu onClick={clickHandler} className={menuClassName}>
            {renderMessage}
            {options.map((item) => {
              const { key, display, disabled, onClick, type, className } = item

              return (
                <MenuItem
                  onClick={onClick}
                  disabled={disabled}
                  key={key || item}
                  type={type}
                  className={className}
                >
                  {display || item}
                </MenuItem>
              )
            })}
          </Menu>
        )
      }
    >
      {children && children.length !== 0 ? (
        <span>{children}</span>
      ) : (
        <span>
          <Button link={link} type={type} size={size} disabled={disabled}>
            {text}
            <Icon icon={icon} fill={iconFill} />
          </Button>
        </span>
      )}
    </Dropdown>
  )
}

DropdownMenu.defaultProps = {
  size: 'medium',
  type: 'secondary',
  link: false,
  trigger: 'hover',
  clickHandler: null,
  message: null,
  disabled: false,
  icon: 'FaCaretDown',
}

DropdownMenu.propTypes = {
  /** Dropdown children renders after menu items and children are wrapped in a SPAN */
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.node,
  ]),
  /** Menu Items */
  options: PropTypes.array,
  /** Menu Item onClick handler */
  clickHandler: PropTypes.func,
  /** Classname */
  className: PropTypes.string,
  /** Text for the default button type secondary. Not needed if using children */
  text: PropTypes.string,
  /** Size of the button, default is medium. Not needed if using children */
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  /** Button type, default is secondary. Not needed if using children */
  type: PropTypes.oneOf(['primary', 'secondary']),
  /** Style Button link a link */
  link: PropTypes.bool,
  /** trigger to open dropdown. defaults to hover */
  trigger: PropTypes.oneOf(['click', 'hover']),
  /** Message to show at top of dropdown menu */
  message: PropTypes.string,
  disabled: PropTypes.bool,
  placement: PropTypes.string,
  icon: PropTypes.string,
  iconFill: PropTypes.string,
  overlay: (props, name, component) => {
    if (props.options === undefined && props[name] === undefined) {
      return new Error('Either options array or overlay component required')
    }
    return null
  },
}

export default compose(withStyle)(DropdownMenu)
